import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";
import { LocaleProvider } from "./i18n";
import { AppLayout, OverviewPage, ProfilePage, ServicesPage, SettingsPage } from "./pages/app";
import { BookPage, ManageBookingPage } from "./pages/book";
import { BookingsPage } from "./pages/bookings";
import { DirectoryPage } from "./pages/directory";
import { InsightsPage } from "./pages/insights";
import { InvitePage } from "./pages/invite";
import { LegalPage } from "./pages/legal";
import { NewClinicPage } from "./pages/new-clinic";
import { PractitionersPage } from "./pages/practitioners";
import {
  ForgotPasswordPage,
  LandingPage,
  LoginPage,
  NotFoundPage,
  ResetPasswordPage,
  SignupPage,
} from "./pages/public";
import { TeamPage } from "./pages/team";
import { createQueryClient } from "./query-client";
import "./styles.css";

// The clinic app. The platform admin console is a separate app on its own host (control.tsx).
const queryClient = createQueryClient();

const router = createBrowserRouter([
  { path: "/", element: <LandingPage /> },
  { path: "/signup", element: <SignupPage /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  { path: "/clinics", element: <DirectoryPage /> },
  { path: "/invite/:token", element: <InvitePage /> },
  { path: "/privacy", element: <LegalPage id="privacy" /> },
  { path: "/terms", element: <LegalPage id="terms" /> },
  { path: "/dpa", element: <LegalPage id="dpa" /> },
  { path: "/cookies", element: <LegalPage id="cookies" /> },
  // Public booking pages, addressed by clinic.
  { path: "/book/:slug", element: <BookPage /> },
  { path: "/book/:slug/manage/:token", element: <ManageBookingPage /> },
  {
    path: "/app",
    element: <AppLayout />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: "bookings", element: <BookingsPage /> },
      { path: "practitioners", element: <PractitionersPage /> },
      { path: "services", element: <ServicesPage /> },
      { path: "insights", element: <InsightsPage /> },
      { path: "team", element: <TeamPage /> },
      { path: "settings", element: <SettingsPage /> },
      { path: "clinics/new", element: <NewClinicPage /> },
      { path: "profile", element: <ProfilePage /> },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <LocaleProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </LocaleProvider>
  </StrictMode>,
);
